import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

/**
 * Header component provides site navigation and branding.
 *
 * Features:
 * - Fixed position header with backdrop blur
 * - Responsive mobile menu
 * - Smooth scrolling to page sections
 * - External link to main application
 * - Mobile-first responsive design
 *
 * @component
 * @example
 * ```tsx
 * <Header />
 * ```
 */
const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Navigates to a section, handling both same-page scrolling and cross-page navigation
   * @param sectionId - The ID of the target section
   */
  const navigateToSection = (sectionId: string): void => {
    setIsMenuOpen(false);
    
    // If we're already on the home page, just scroll to the section
    if (location.pathname === '/') {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Navigate to home page with the section hash
      navigate('/', { replace: true });
      // Wait a bit for navigation to complete, then scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-secondary-200" role="banner">
      <div className="container-padding">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2" aria-label="TherapyLink Home">
            <img
              src="/images/tl-1-64.ico"
              alt="TherapyLink Logo"
              className="w-8 h-8"
              width="32"
              height="32"
              loading="eager"
            />
            <span className="text-2xl font-bold text-secondary-900" aria-hidden="true">
              TherapyLink
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8" aria-label="Main navigation">
            <button
              onClick={() => navigateToSection('features')}
              className="text-secondary-600 hover:text-primary-600 transition-colors duration-200"
              aria-label="Navigate to Features section"
            >
              Features
            </button>
            <button
              onClick={() => navigateToSection('workflow')}
              className="text-secondary-600 hover:text-primary-600 transition-colors duration-200"
              aria-label="Navigate to How It Works section"
            >
              How It Works
            </button>
            {/* <button
              onClick={() => navigateToSection('case-studies')}
              className="text-secondary-600 hover:text-primary-600 transition-colors duration-200"
            >
              Case Studies
            </button> */}
            <button
              onClick={() => navigateToSection('contact')}
              className="text-secondary-600 hover:text-primary-600 transition-colors duration-200"
              aria-label="Navigate to Contact section"
            >
              Contact
            </button>
            {/* <a
              href="https://dev.therapy-link.org"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
            >
              Sign In
            </a> */}
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-lg text-secondary-600 hover:text-primary-600 hover:bg-secondary-50 transition-colors duration-200"
            aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-navigation"
          >
            {isMenuOpen ? (
              <XMarkIcon className="w-6 h-6" />
            ) : (
              <Bars3Icon className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div id="mobile-navigation" className="md:hidden py-4 border-t border-secondary-200">
            <nav className="flex flex-col space-y-4" aria-label="Mobile navigation">
              <button
                onClick={() => navigateToSection('features')}
                className="text-left text-secondary-600 hover:text-primary-600 transition-colors duration-200"
              >
                Features
              </button>
              <button
                onClick={() => navigateToSection('workflow')}
                className="text-left text-secondary-600 hover:text-primary-600 transition-colors duration-200"
              >
                How It Works
              </button>
              <button
                onClick={() => navigateToSection('contact')}
                className="text-left text-secondary-600 hover:text-primary-600 transition-colors duration-200"
              >
                Contact
              </button>
              <a
                href="https://dev.therapy-link.org"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary w-fit"
              >
                Sign In
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
